// Tipos principales para el sistema Grade MCP
export interface MCPEvaluation {
  id: string;
  query: string;
  timestamp: string;
  status: 'completed' | 'failed' | 'pending' | 'in-progress';
  response: string;
  duration: string;
  score?: number; // Legacy field
  overall_score?: number; // New field from API
  percentage_score?: number;
  grade?: string;
  model?: string;
  rubric_type?: string;
  rubric_id?: string;
  is_liked?: boolean;
  is_training_data?: boolean;
  deliverable_id?: string | null;
  source?: string;
  processing_time_ms?: number;
  confidence_score?: number | null;
  is_flagged?: boolean;
  is_reviewed?: boolean;
  workspace_id?: string | null;

  // 🆕 Nuevos campos para datos detallados del backend
  feedback?: string; // Feedback textual del backend
  criteria_scores?: any; // Scores por criterio (JSON del backend)
  evaluation_data?: any; // Datos completos de evaluación (JSON del backend)

  // 🆕 Campos de mejora del backend
  improved_query?: string;
  improvement_explanation?: string;
  improvement_model?: string;
  improvement_timestamp?: string;
  improvement_data?: Record<string, any>;
  has_improvement?: boolean;

  // 🆕 Campos para datos estructurados del frontend
  evaluation?: {
    overall_score: number;
    feedback: string;
    criteria_scores: Array<{
      name: string;
      score: number;
      weight: number;
      description: string;
    }>;
  };
  improvement_text?: string; // Texto de mejora generado
}

// Helper function to get score from evaluation (prioritizing overall_score)
export function getEvaluationScore(evaluation: MCPEvaluation): number | undefined {
  return evaluation.overall_score ?? evaluation.score;
}

export interface MCPTask {
  id: string;
  name: string;
  description: string;
  category: TaskCategory;
  categoryId?: number; // Añadido del backend
  categoryName?: string; // Añadido del backend
  status: TaskStatus;
  createdAt: string;
  lastRun?: string;
  executions: number;
  successRate: number;
  rubric: string;
  enabled?: boolean;
}

export interface MCPModel {
  id: string;
  name: string;
  type: string;
  trainingData: number;
  accuracy: number;
  status: ModelStatus;
  progress?: number;
  endpoint?: string;
  config?: Record<string, any>;
  categories_id?: number[];
  tasks?: MCPTask[];
  categories?: string[]; // Calculado desde las tasks
}

export interface TaskExecution {
  id: string;
  taskId: string;
  timestamp: string;
  input: string;
  output: string;
  score: number;
  success: boolean;
  duration: number;
  model: string;
}

export interface TrainingData {
  id: string;
  prompt: string;
  completion: string;
  score: number;
  approved: boolean;
  timestamp: string;
  modelId?: string;
}

// Tipos de estado
export type TaskStatus = 'active' | 'draft' | 'paused' | 'archived';
export type ModelStatus = 'training' | 'completed' | 'failed' | 'deployed';
export type TaskCategory =
  | 'Comunicación'
  | 'Ventas'
  | 'Análisis'
  | 'Finanzas'
  | 'General';

// Interfaces para requests/responses de la API
export interface EvaluationRequest {
  text: string;
  context?: string;
  rubric_type?: string;
  model?: string;
}

export interface EvaluationResponse {
  score: number;
  feedback: string;
  suggestions: string[];
  rubric_used: string;
  timestamp: string;
}

export interface TaskCreateRequest {
  name: string;
  description: string;
  category: TaskCategory;
  rubric: string;
}

export interface TaskUpdateRequest extends Partial<TaskCreateRequest> {
  id: string;
  status?: TaskStatus;
}

// Filtros y búsqueda
export interface EvaluationFilters {
  status?: string;
  dateFrom?: string;
  dateTo?: string;
  model?: string;
  scoreRange?: [number, number];
  search?: string;
  is_liked?: boolean;
  is_training_data?: boolean;
  is_auto?: boolean; // Para filtrar evaluaciones marcadas automáticamente (score >= 8)
}

export interface TaskFilters {
  category?: TaskCategory | 'Todos';
  status?: TaskStatus;
  search?: string;
}

// Nuevas interfaces para respuestas paginadas
export interface PaginatedTasksResponse {
  tasks: MCPTask[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Estados de la aplicación
export interface DashboardStats {
  evaluations: {
    total: number;
    completed: number;
    pending: number;
    failed: number;
    averageScore: number;
  };
  tasks: {
    total: number;
    active: number;
    completed: number;
    draft: number;
  };
  models: {
    total: number;
    training: number;
    deployed: number;
    draft: number;
  };
  trainingData: {
    totalSamples: number;
    highQualitySamples: number;
    averageScore: number;
  };
  system: {
    uptime: string;
    memoryUsage: number;
    cpuUsage: number;
  };
}

// Error handling
export interface APIError {
  message: string;
  code?: string;
  details?: any;
}

// Hook states
export interface UseEvaluationsState {
  evaluations: MCPEvaluation[];
  loading: boolean;
  error: APIError | null;
  total: number;
  page: number;
  pageSize: number;
}

export interface UseTasksState {
  tasks: MCPTask[];
  loading: boolean;
  error: APIError | null;
  selectedTask: MCPTask | null;
}

export interface UseModelsState {
  models: MCPModel[];
  loading: boolean;
  error: APIError | null;
}

// Nuevas interfaces para TasksTab
export interface TaskTreeNode {
  id: number;
  name: string;
  parentId?: number;
  hasChildren: boolean;
  taskCount: number;
  children: TaskTreeNode[];
}

export interface TaskTreeResponse {
  success: boolean;
  categories: TaskTreeNode[];
  total_tasks: number;
  total_categories: number;
}

export interface TaskHierarchyItem {
  id: string;
  name: string;
  type: 'task' | 'category';
  description?: string;
  status?: string;
  createdAt?: string;
  lastRun?: string;
  executions?: number;
  successRate?: number;
  rubric?: any;
  children?: TaskHierarchyItem[];
}
